import React, { useState, useEffect, useRef, useMemo } from 'react';

import BulkRequestDocumentForm from '../../../../bulk/request/document/form';

import DocumentFormContext from '../../../../document/context/document_form_context';

import { fetchCustomFieldValue, isAuthorizedCustomFieldToUser } from "../../../../helper/custom_field/helper";

import { index as indexLaboralHiring } from "../../../hiring/axios";
import { create as createBulkRequest } from "./axios";

const LaboralBulkRequestHiringDocumentForm = (props) => {
  const {
    bulkRequest,
    currentUser,
    currentCustomer,
    data: {
      companies,
      dateFormats,
      hiringCustomFields,
      employeeCustomFields,
      securityLayers
    }
  } = props;

  const context = "laboral_bulk";

  const actionsProps = {
    indexEntities: indexLaboralHiring,
    createBulkRequest: createBulkRequest,
    indexEntitiesPath: Routes.laboral_hirings_path,
    showBulkRequestPath: Routes.laboral_bulk_request_hiring_document_path,
    indexBulkRequestPath: Routes.laboral_bulk_request_hiring_documents_path
  }

  const hiringStateOptions = [ {value: 'active', label: I18n.t('laboral.hirings.index.tabs.active') }, { value: 'expired', label: I18n.t('laboral.hirings.index.tabs.expired') } ]

  const defaultRequestParams = {};

  // Start UseRef
  const bootstrapTableRef = useRef(null);

  const entityConfiguration = {
    key: 'hirings',
    form: {
      entityItemsAttributes: {
        key: 'hiring_items_attributes',
        entityId: 'hiring_id'
      },
    },
    label: {
      singular: I18n.t("laboral.bulk.request.hiring_documents.form.tabs.hiring"),
      plural: I18n.t("laboral.bulk.request.hiring_documents.form.tabs.hirings"),
    },
    navButtonTitle: I18n.t("laboral.bulk.request.hiring_documents.form.tabs.hirings_title")
  }

  const documentFormContextValue = {
    currentUser: currentUser || {},
    currentCustomer: currentCustomer || {},
    isPersisted: false,
    context: "laboral_bulk",
    data: props?.data,
    entities: [],
  }

  const entityHeaderConfigTab = useMemo(() => {
    return _.flattenDeep([
      {
        label: I18n.t('activerecord.attributes.laboral/hiring.identifier'),
        key: 'identifier'
      },{
        label: I18n.t('activerecord.attributes.laboral/hiring.company'),
        key: 'company.name'
      },{
        label: I18n.t('activerecord.attributes.laboral/hiring.employee'),
        key: 'employee.name'
      },{
        label: I18n.t('activerecord.attributes.laboral/hiring.start_date'),
        key: 'start_date_to_s'
      },{
        label: I18n.t('activerecord.attributes.laboral/hiring.end_date'),
        key: 'end_date_to_s'
      },
       _.map(hiringCustomFields, customField => {
        return {
          label: customField.label,
          key: `custom_fields.${ customField.code }`
        }
      })
    ])
  }, [])

  // Start Getter

  const tableColumnsDefault = [
    {
      field: 'company.name',
      header: I18n.t("activerecord.attributes.laboral/hiring.company"),
      filter: {
        enabled: true,
        placeholder: 'Seleccione una empresa',
        inputType: 'simple_select',
        filterrific: {
          search: 'by_company',
        },
        options: _.map(companies, company => ({
          value: company.id,
          label: company.name,
        })),
      },
    },
    {
      field: 'employee.identifier',
      header: I18n.t("activerecord.attributes.laboral/employee.identifier"),
      filter: {
        enabled: true,
        placeholder: 'Ingrese el identificador del empleado',
        inputType: 'text',
        filterrific: {
          search: 'search_by_employee_identifier'
        }
      },
    },
    {
      field: 'employee.name',
      header: I18n.t("activerecord.attributes.laboral/hiring.employee"),
      filter: {
        enabled: true,
        placeholder: 'Ingrese el nombre del empleado',
        inputType: 'text',
        filterrific: {
          search: 'search_by_name'
        }
      },
      sort: {
        enabled: true,
        filterrific: {
          sort: 'employee_name_'
        }
      }
    },
    {
      field: 'identifier',
      header: I18n.t("activerecord.attributes.laboral/hiring.identifier"),
      filter: {
        enabled: true,
        placeholder: 'Ingrese el identificador de la contratación',
        inputType: 'text',
        filterrific: {
          search: 'search_query'
        }
      },
    },
    {
      field: 'active_to_s',
      header: 'Estado de Contratación',
      filter: {
        enabled: true,
        placeholder: 'Seleccione un estado de contratación',
        inputType: 'simple_select',
        options: _.map(hiringStateOptions, hiringState => ({
          value: hiringState.value,
          label: hiringState.label,
        })),
        filterrific: {
          search: 'by_hiring_state'
        }
      },
    },
    {
      field: 'start_date_to_s',
      header: I18n.t("activerecord.attributes.laboral/hiring.start_date"),
      filter: {
        enabled: true,
        placeholder: 'Ingrese la fecha de inicio de la contratación',
        inputType: 'text',
        filterrific: {
          search: 'search_by_start_date'
        }
      },
      sort: {
        enabled: true,
        filterrific: {
          sort: 'start_date_'
        }
      }
    },
    {
      field: 'end_date_to_s',
      header: I18n.t("activerecord.attributes.laboral/hiring.end_date"),
      body: (row) => row?.end_date_to_s || 'Indefinido'
    },
  ]

  const tableColumnSecurityLayer = useMemo(() => {
    if(currentCustomer.security_layer && securityLayers?.length > 0){
      return {
        field: 'employee.security_layer.name',
        header: I18n.t("activerecord.attributes.laboral/employee.security_layer_id"),
        body: (row) => row?.employee?.security_layer?.name || I18n.t('no_entry'),
        filter: {
          enabled: true,
          placeholder: 'Seleccione una capa de seguridad',
          inputType: 'simple_select',
          options: _.map(securityLayers, securityLayer => ({
            value: securityLayer.id,
            label: securityLayer.name,
          })),
          filterrific: {
            search: 'by_employee_security_layer_id'
          }
        }
      }
    }
  })

  const tableColumnsCustomFields = useMemo(() => {
    return _.map(hiringCustomFields, function (customField) {
      return {
        field: `custom_fields.${ customField.code }`,
        header: _.capitalize(customField.label),
        body: (row) => {
          return fetchCustomFieldValue(customField, row.custom_fields[customField.code], currentUser)
        },
        filter: {
          enabled: isAuthorizedCustomFieldToUser(customField, currentUser),
          placeholder: `Ingrese ${ _.capitalize(customField.label) }`,
          filterrific: {
            search: `search_by_key_custom_fields`,
            searchKey: customField.code
          }
        },
        configuration: {
          isCustomField: true
        }
      }
    })
  }, [hiringCustomFields])

  const tableColumns = useMemo(() => {

    let columns = _.concat(
      tableColumnSecurityLayer,
      tableColumnsDefault,
      tableColumnsCustomFields
    )

    return _.compact(columns)
  }, [])

  return(
    <DocumentFormContext.Provider value={ documentFormContextValue }>
      <div className="row">
        <div className="col-12">
          <BulkRequestDocumentForm
            bulkRequest={ bulkRequest }
            dataTableConfiguration={{
              columns: tableColumns
            }}
            configuration={{
              defaultRequestParams: defaultRequestParams,
              entity: entityConfiguration,
              entityHeaderConfigTab: entityHeaderConfigTab,
              context: context,
              formName: 'laboral_bulk_request_hiring_document',
              shortlist: {
                enabled: currentCustomer?.shortlist_integration_enabled || false
              }
            }}
            actions={
              actionsProps
            }
            data={{
              ... props?.data,
              currentUser: currentUser,
              customFields: _.concat(hiringCustomFields, employeeCustomFields)
            }}
          />
        </div>
      </div>
    </DocumentFormContext.Provider>
  )
}

export default LaboralBulkRequestHiringDocumentForm;
